import { useCallback } from "react";

import {
  IconButton,
  Menu,
  MenuList,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { useMediaQuery } from "@braintrust/braintrust-ui-components";
import {
  FlagIcon,
  MoreVertIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import type { ReportPayload } from "@js/apps/common/forms/report";
import { useAccountType } from "@js/apps/common/hooks/use-account-type";
import {
  useLazyGetEmployerReviewsQuery,
  useLazyGetTalentReviewsQuery,
  useReportReviewMutation,
} from "@js/apps/reviews/api";
import { DEFAULT_REVIEWS_COUNT } from "@js/apps/reviews/constants";
import { Snackbar } from "@js/components/snackbar";

import { openReportReviewModalInstance } from "../review-card-modal-content";

type ReviewCardActionMenuProps = {
  id: number;
  isOwnProfile: boolean;
  profileId: number;
};

export const ReviewCardActionMenu = ({
  id,
  isOwnProfile,
  profileId,
}: ReviewCardActionMenuProps) => {
  // matches the breakpoint at which Menu switches to the drawer
  const isDrawer = useMediaQuery("sm");
  const { isEmployer } = useAccountType();
  const [reportReview] = useReportReviewMutation();
  const [triggerGetTalentReviews] = useLazyGetTalentReviewsQuery();
  const [triggerGetEmployerReviews] = useLazyGetEmployerReviewsQuery();

  const getReviews = isEmployer
    ? triggerGetEmployerReviews
    : triggerGetTalentReviews;

  const handleReportReview = useCallback(
    (reportReason: ReportPayload) => {
      reportReview({ id, data: reportReason })
        .unwrap()
        .then(() => {
          void getReviews(
            {
              id: profileId,
              count: DEFAULT_REVIEWS_COUNT,
            },
            false,
          );
          Snackbar.success("Review reported.");
        })
        .catch(() => Snackbar.error("Failed to report the review."));
    },
    [id, reportReview, profileId, getReviews],
  );

  if (!isOwnProfile) {
    return null;
  }

  const reportItem = (
    <Menu.Item
      onClick={() => openReportReviewModalInstance(handleReportReview)}
    >
      <FlagIcon sx={{ color: "var(--negative-1)" }} />
      <Typography component="span" color="negative" sx={{ ml: 1 }}>
        Report review
      </Typography>
    </Menu.Item>
  );

  return (
    <Menu
      drawerOnMobile={isDrawer}
      anchor={
        <IconButton
          style={{ padding: 0 }}
          aria-label="profile-review-menu"
          aria-haspopup="true"
          variant="transparent"
          size="medium"
        >
          <MoreVertIcon style={{ color: "inherit" }} />
        </IconButton>
      }
    >
      {/* TODO: remove MenuList once BUC wraps drawer items in MenuList itself */}
      {isDrawer ? <MenuList sx={{ p: 0 }}>{reportItem}</MenuList> : reportItem}
    </Menu>
  );
};
